// SEQ-240 Stage B: "How is this worked out" text for the P&L Analytics page.
//
// Three kinds of figure, one module, so a reviewer editing the page finds the
// text in one place:
//
// 1. describeWidget: any widget a dashboard holds (the eight Company overview
//    KPI cards and every widget a user builds). Composed from describeMetric
//    (pnlMetricDescriptions.ts) plus the widget's own config: chart type,
//    group by, aggregation, date range, compare to, sort, limit, filters.
// 2. PNL_SPECIAL_WIDGET_FIGURES: the five built-in widgets that render through
//    their own components (PnlWidgetRenderer.tsx, PHASE1_WIDGET_IDS).
// 3. performanceColumnFigure: the 18 Project performance table columns, in the
//    Totals and By month views.
//
// WHAT IT DESCRIBES (line numbers at commit 9f6bb43):
// - widgetPresentation.ts 30-45 and 136-154: a KPI on one of the rollup-bound
//   metrics reads the company total built by adding up project rows
//   (rollup.ts sumRollups), ratios divided from those totals, whatever the
//   widget's aggregation says; any other metric is folded by the aggregation.
// - dashboardRequestKeys.ts 129-148 and 187-205: the date range (the page's,
//   unless dateRangeMode is "widget") and the project and status filters (the
//   page's, narrowed by the widget's own, never widened).
// - fiscalYear.ts comparisonPeriod: a comparison is the same whole calendar months,
//   shifted one month, quarter or year earlier (SEQ-240).
// - groupByVocabulary.ts: what one bar, point or row stands for.
// - wagesMultiplierGrain.ts: month-grain cost and profit carry raw wages.
// - The RPC's project visibility, 20261006000100_seq193_pnl_project_month_grain.sql
//   122-136: member, creator, or organisation admin.
// - PnlProfitScatterCharts.tsx, PnlProfitByProjectBar.tsx,
//   PnlMonthlyTrendChart.tsx, PnlProjectPerformanceTable.tsx and
//   PnlProjectMonthGrid.tsx for the five special widgets and the columns.
//
// Tested by pnlWidgetDescriptions.test.ts (npm run verify:pnl-widget-descriptions).

import type { MetricDefinition, MetricSourceKind } from "../../components/howWorkedOut/metricDefinition";
import { PNL_METRIC_KEYS, resolveAggregationMethod, resolveMetricUnit } from "../../lib/pnl/pnlAnalytics";
import { monthOnMonthMetricKeys, type PnlWidgetConfig } from "../../lib/pnl/pnlWidgetSchema";
import { RAW_WAGES_NOTE, showsRawWagesNote } from "../../lib/pnl/wagesMultiplierGrain";
import { groupByExplainer, groupingCaption } from "./groupByVocabulary";
import type { PnlMetricOption } from "./metricOption";
import { MONTH_ON_MONTH_MAX_METRICS, MONTH_ON_MONTH_MAX_PROJECTS } from "./monthOnMonth";
import { describeMetric, metricUsesPayroll, type DescribableMetric, type DescribeContext } from "./pnlMetricDescriptions";
import { AGGREGATION_HINTS, AGGREGATION_LABELS, CHART_TYPE_OPTIONS, DATE_PRESET_LABELS } from "./widgetEditorState";
import { ROLLUP_BINDING_BY_METRIC } from "./widgetPresentation";

/** Who can see which projects on this page, said once. */
const VISIBLE_PROJECTS_DETAIL =
  "Only projects you are a member of, created, or can see as an organisation admin. The Projects and Status filters at the top narrow it.";

const PAGE_PROJECTS: MetricDefinition["projects"] = { scope: "all-unless-filtered", detail: VISIBLE_PROJECTS_DETAIL };
const PAGE_DATES: MetricDefinition["dates"] = { scope: "page-range" };

/** The footer's own wording and formatting, so the pop-up and the page footer show the same time. */
export function payrollFreshness(payrollAsAt: string | null | undefined): string | undefined {
  if (!payrollAsAt) return undefined;
  const at = new Date(payrollAsAt);
  if (Number.isNaN(at.getTime())) return undefined;
  return `Payroll data as at ${at.toLocaleString("en-AU")}`;
}

const COMPARISON_LINES: Record<string, string> = {
  previous_month: "the same whole calendar months, one month earlier",
  previous_quarter: "the same whole calendar months, one quarter earlier",
  previous_year: "the same whole calendar months, one year earlier",
  previous_financial_year: "the same whole calendar months, one year earlier",
};

function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

function chartTypeLabel(chartType: string): string {
  return CHART_TYPE_OPTIONS.find((option) => option.value === chartType)?.label ?? "Chart";
}

/** The widget's own project and status filters, in words, or nothing when it has none. */
function widgetFilterDetail(config: PnlWidgetConfig): string | null {
  const parts: string[] = [];
  for (const filter of config.filters) {
    const values = Array.isArray(filter.value) ? filter.value.map(String) : [String(filter.value)];
    if (filter.field === "status") parts.push(`This widget also keeps only ${values.join(" or ")} projects.`);
    if (filter.field === "projectId" || filter.field === "project_id") {
      parts.push(`This widget also keeps only the ${values.length} project${values.length === 1 ? "" : "s"} picked in its settings.`);
    }
  }
  return parts.length > 0 ? parts.join(" ") : null;
}

function widgetDates(config: PnlWidgetConfig): MetricDefinition["dates"] {
  if (config.dateRangeMode !== "widget") return PAGE_DATES;
  const own =
    typeof config.dateRange.customPresetId === "string"
      ? "A date range saved on this dashboard"
      : config.dateRange.preset === "custom" && config.dateRange.custom
        ? `${config.dateRange.custom.from ?? "the start"} to ${config.dateRange.custom.to ?? "today"}`
        : DATE_PRESET_LABELS[config.dateRange.preset];
  return { scope: "own-window", detail: `${own}. The date range at the top of the page does not change it.` };
}

function widgetPeriod(config: PnlWidgetConfig): string {
  if (config.chartType === "KPI" || config.groupBy === "company") return "One figure for the whole date range.";
  if (config.chartType === "month_on_month") return "One column per calendar month in the date range.";
  return groupByExplainer(config.groupBy).hint;
}

/** How each bar, point or row folds its project months, for a widget that is not a KPI. */
function bucketLine(config: PnlWidgetConfig, metric: DescribableMetric | undefined): string {
  const term = groupByExplainer(config.groupBy).bucketTerm;
  if (metric?.kind === "calculated") {
    return `Each ${term} works the figure out from its own totals, so the aggregation setting does not change it.`;
  }
  // SEQ-240 audit B, row 20: a chart reads each bucket's total from the
  // database, so a sum-family metric saved with Average, Minimum, Maximum or
  // Latest still shows the total. Say so rather than name the saved setting.
  if (metric && resolveAggregationMethod(metric) === "sum" && config.aggregation !== "sum") {
    return `Each ${term} shows the total of the project months inside it. The ${AGGREGATION_LABELS[config.aggregation].toLowerCase()} setting saved on this widget does not change it.`;
  }
  const label = AGGREGATION_LABELS[config.aggregation].toLowerCase();
  const hint = AGGREGATION_HINTS[config.aggregation];
  return `Each ${term} shows the ${label} of the project months inside it.${hint ? ` ${hint}` : ""}`;
}

/** The KPI card's fold, said the way resolveWidgetValue does it. */
function kpiLine(config: PnlWidgetConfig, metric: DescribableMetric | undefined): string {
  if (ROLLUP_BINDING_BY_METRIC[config.metric]) {
    const unit = metric ? resolveMetricUnit(metric) : "currency";
    return unit === "percent"
      ? "This card adds up every project over the date range first, then works the percentage out from those totals. It is not an average of each project's percentage."
      : "This card adds up every project over the date range.";
  }
  const label = AGGREGATION_LABELS[config.aggregation].toLowerCase();
  const hint = AGGREGATION_HINTS[config.aggregation];
  return `This card shows the ${label} across the project months in range.${hint ? ` ${hint}` : ""}`;
}

function mergeSource(primary: MetricDefinition["source"], secondary?: MetricDefinition["source"]): MetricDefinition["source"] {
  if (!secondary || secondary.kind === primary.kind) return primary;
  const kind: MetricSourceKind = "mixed";
  return { kind, detail: primary.detail === secondary.detail ? primary.detail : `${primary.detail} ${secondary.detail}` };
}

export interface WidgetDescribeOptions {
  /** dataset.meta.payrollAsAt from the page, for the Last updated row of payroll figures. */
  payrollAsAt?: string | null;
}

/** The pop-up text for one dashboard widget, built from its metric and its own settings. */
export function describeWidget(
  config: PnlWidgetConfig,
  metrics: readonly PnlMetricOption[],
  options: WidgetDescribeOptions = {},
): MetricDefinition {
  const byKey = new Map(metrics.map((m) => [m.metricKey, m]));
  const metricFor = (key: string): DescribableMetric => byKey.get(key) ?? { metricKey: key, section: "calculated", kind: "calculated" };
  const filterDetail = widgetFilterDetail(config);
  const context: DescribeContext = {
    projects: { ...PAGE_PROJECTS, detail: filterDetail ? `${VISIBLE_PROJECTS_DETAIL} ${filterDetail}` : VISIBLE_PROJECTS_DETAIL },
    dates: widgetDates(config),
    period: widgetPeriod(config),
  };

  const isMonthOnMonth = config.chartType === "month_on_month";
  const rowKeys = isMonthOnMonth && config.groupBy !== "project" ? monthOnMonthMetricKeys(config) : [config.metric];
  const primary = describeMetric(metricFor(rowKeys[0]), context);
  const secondaryKey = config.chartType === "KPI" || isMonthOnMonth ? undefined : config.secondaryMetric;
  const secondary = secondaryKey ? describeMetric(metricFor(secondaryKey), context) : undefined;

  const formula: string[] = [];
  if (config.chartType === "KPI") {
    formula.push(...primary.formula, kpiLine(config, byKey.get(config.metric)));
  } else if (isMonthOnMonth) {
    if (config.groupBy === "project") {
      formula.push(`One row per project picked in the widget's settings (1 to ${MONTH_ON_MONTH_MAX_PROJECTS}), showing ${primary.name}.`);
    } else {
      const names = rowKeys.map((key) => describeMetric(metricFor(key)).name);
      formula.push(`One row per chosen metric (up to ${MONTH_ON_MONTH_MAX_METRICS}): ${names.slice(0, MONTH_ON_MONTH_MAX_METRICS).join(", ")}.`);
    }
    formula.push("One column per calendar month. A month still in progress is marked and left out of the change.");
    if (config.showChange) formula.push("Change: the last complete month minus the one before it, in the metric's unit and as a percentage of the month before.");
    formula.push("Each row's own explanation sits beside its name.");
  } else {
    formula.push(`${chartTypeLabel(config.chartType)}. ${groupingCaption(config.chartType, config.groupBy) ?? ""}`.trim());
    if (config.chartType === "scatter" && secondary) {
      formula.push(`Across: ${primary.name}. Up: ${secondary.name}.`);
    } else if (secondary) {
      formula.push(`Shows ${primary.name} and ${secondary.name} together.`);
    }
    formula.push(...primary.formula);
    if (secondary) formula.push(`${secondary.name}: ${lowerFirst(secondary.formula[0])}`);
    formula.push(bucketLine(config, byKey.get(config.metric)));
    if (config.sort) formula.push(config.sort.direction === "desc" ? "Sorted highest first." : "Sorted lowest first.");
    if (typeof config.limit === "number") formula.push(`Shows the first ${config.limit} only.`);
  }
  if (config.comparison && !isMonthOnMonth) {
    const how = config.chartType === "KPI" ? "The change under the number" : "The second series";
    formula.push(`${how} compares with ${COMPARISON_LINES[config.comparison.kind] ?? "an earlier period of the same length"}.`);
  }
  const rawWages =
    showsRawWagesNote(config.groupBy, config.metric) ||
    (secondaryKey !== undefined && showsRawWagesNote(config.groupBy, secondaryKey));
  if (!isMonthOnMonth && rawWages) formula.push(RAW_WAGES_NOTE);

  const usesPayroll = rowKeys.some(metricUsesPayroll) || (secondaryKey !== undefined && metricUsesPayroll(secondaryKey));
  return {
    name: config.name,
    meaning: isMonthOnMonth && rowKeys.length > 1 ? "Each chosen metric, month by month." : primary.meaning,
    formula,
    source: mergeSource(primary.source, secondary?.source),
    projects: context.projects!,
    dates: context.dates!,
    period: context.period!,
    lastUpdated: usesPayroll ? payrollFreshness(options.payrollAsAt) : undefined,
  };
}

/** The projects and dates any widget covers: the page's, narrowed by the widget's own filter and date range. */
export function widgetScope(config: PnlWidgetConfig): Pick<MetricDefinition, "projects" | "dates"> {
  const filterDetail = widgetFilterDetail(config);
  return {
    projects: { ...PAGE_PROJECTS, detail: filterDetail ? `${VISIBLE_PROJECTS_DETAIL} ${filterDetail}` : VISIBLE_PROJECTS_DETAIL },
    dates: widgetDates(config),
  };
}

/** The projects and dates every row of a month on month widget shares, for its row-label pop-ups. */
export function monthOnMonthRowContext(config: PnlWidgetConfig): DescribeContext {
  const filterDetail = widgetFilterDetail(config);
  return {
    projects: { ...PAGE_PROJECTS, detail: filterDetail ? `${VISIBLE_PROJECTS_DETAIL} ${filterDetail}` : VISIBLE_PROJECTS_DETAIL },
    dates: widgetDates(config),
    period: "One column per calendar month in the date range.",
  };
}

// ─── The five built-in widgets ──────────────────────────────────────────────

const PIN_LINE = "A project far outside the rest is drawn at the edge of the chart and listed under it with its real figures.";
const CLAIMS_TYPED_PAYROLL =
  "Income from the progress claims, costs typed in on each project's P&L tab, overtime and hours from payroll (KeyPay) or typed in. Xero is not counted.";
const CLAIMS_TYPED = "Income from the progress claims, costs typed in on each project's P&L tab. Xero is not counted.";
const PROFIT_PCT_LINE = "Profit %: the project's net profit divided by its revenue, times 100.";

/** Static text for the five built-in widgets, keyed by their widget id. Payroll freshness is added by the caller. */
export const PNL_SPECIAL_WIDGET_FIGURES = {
  "scatter-profit-vs-overtime": {
    name: "Profit vs overtime",
    meaning: "Each project's profit margin set against how much overtime it worked.",
    formula: [
      "One point per project over the date range.",
      `Up: ${lowerFirst(PROFIT_PCT_LINE)}`,
      "Across, chosen with the toggle: Overtime hours % (overtime hours divided by all labour hours, times 100), Overtime hours (as worked), or Overtime cost % revenue (overtime pay divided by revenue, times 100).",
      "A project with no figure on either axis is left off.",
      PIN_LINE,
    ],
    source: { kind: "mixed", detail: CLAIMS_TYPED_PAYROLL },
    projects: PAGE_PROJECTS,
    dates: PAGE_DATES,
    period: "Each project over the whole date range.",
  },
  "scatter-profit-vs-patching": {
    name: "Profit vs patching",
    meaning: "Each project's profit margin set against how much it spent on patching (rework).",
    formula: [
      "One point per project over the date range.",
      `Up: ${lowerFirst(PROFIT_PCT_LINE)}`,
      "Across: Patching % revenue, the typed patching cost divided by revenue, times 100.",
      "A project with no figure on either axis is left off.",
      PIN_LINE,
    ],
    source: { kind: "mixed", detail: CLAIMS_TYPED },
    projects: PAGE_PROJECTS,
    dates: PAGE_DATES,
    period: "Each project over the whole date range.",
  },
  "bar-profit-percent-by-project": {
    name: "Profit % by project",
    meaning: "Each project's profit as a share of its own revenue.",
    formula: [
      "One bar per project over the date range.",
      PROFIT_PCT_LINE,
      "Show: Top 10 (highest first), Bottom 10 (lowest first), or All projects.",
      "A project with no revenue has no percentage and is left off.",
      PIN_LINE,
    ],
    source: { kind: "mixed", detail: CLAIMS_TYPED },
    projects: PAGE_PROJECTS,
    dates: PAGE_DATES,
    period: "Each project over the whole date range.",
  },
  "line-monthly-trend": {
    name: "Monthly trend",
    meaning: "Profit and overtime as shares of revenue, month by month.",
    formula: [
      "One point per calendar month.",
      "Profit %: that month's net profit divided by its revenue, times 100.",
      "Overtime % of revenue: that month's overtime pay from payroll divided by its revenue, times 100.",
      "View: Company total adds every project together; pick a project to see only that one.",
      "Each month uses wages as typed, like the month columns on a project's P&L tab, so months do not match a yearly figure that applies the wages multiplier.",
    ],
    source: { kind: "mixed", detail: CLAIMS_TYPED_PAYROLL },
    projects: PAGE_PROJECTS,
    dates: PAGE_DATES,
    period: "Each calendar month in the date range.",
  },
  "table-project-performance": {
    name: "Project performance",
    meaning: "Every project's income, costs, profit, overtime, patching and hours side by side.",
    formula: [
      "Totals: one row per project over the date range. It counts every project month with income, including months where costs are not entered yet.",
      "By month: a group of columns for each month, then the Total, with an All projects row on top. It needs a bounded date range of at most 24 months.",
      "By month leaves project months with income but no costs entered out of its cells, the All projects row and the Total unless Show months without costs is on, so its profit can be lower than Totals for the same dates.",
      "In By month, Cost and Profit month cells use wages as typed while the Total multiplies them, so the months do not add up to the Total.",
      "Months shown and Show months without costs change which months appear. Both are remembered in this browser only.",
      "Each column heading has its own explanation.",
    ],
    source: { kind: "mixed", detail: CLAIMS_TYPED_PAYROLL },
    projects: PAGE_PROJECTS,
    dates: PAGE_DATES,
    period: "Each project over the date range, or month by month.",
  },
} satisfies Record<string, MetricDefinition>;

/** Which of the special widgets show a payroll figure, so the caller adds the payroll refresh time. */
export const SPECIAL_WIDGETS_USING_PAYROLL: ReadonlySet<string> = new Set([
  "scatter-profit-vs-overtime",
  "line-monthly-trend",
  "table-project-performance",
]);

// ─── Project performance columns ────────────────────────────────────────────

/** The registry metric behind each Project performance column. Project has none. */
export const PERFORMANCE_COLUMN_METRIC: Record<string, string | null> = {
  projectName: null,
  revenue: PNL_METRIC_KEYS.calculated.totalIncome,
  cost: PNL_METRIC_KEYS.calculated.totalCost,
  profit: PNL_METRIC_KEYS.calculated.netProfit,
  profitPercent: PNL_METRIC_KEYS.calculated.profitPercentage,
  overtime: PNL_METRIC_KEYS.labour.overtime,
  overtimePctRevenue: PNL_METRIC_KEYS.calculated.overtimePctRevenue,
  patching: PNL_METRIC_KEYS.expense.patching,
  patchingPctRevenue: PNL_METRIC_KEYS.calculated.patchingPctRevenue,
  patchingPctCost: PNL_METRIC_KEYS.calculated.patchingPctCost,
  ordinaryHours: PNL_METRIC_KEYS.labour.ordinaryHours,
  overtimeHours: PNL_METRIC_KEYS.labour.overtimeHours,
  totalLabourHours: PNL_METRIC_KEYS.calculated.totalLabourHours,
  overtimeHoursPct: PNL_METRIC_KEYS.calculated.overtimeHoursPct,
  overtimePctWages: PNL_METRIC_KEYS.calculated.overtimePctWages,
  labourCostPerHour: PNL_METRIC_KEYS.calculated.labourCostPerHour,
  revenuePerLabourHour: PNL_METRIC_KEYS.calculated.revenuePerLabourHour,
  profitPerLabourHour: PNL_METRIC_KEYS.calculated.profitPerLabourHour,
};

/** The columns whose month cells carry raw wages in By month. */
const RAW_WAGES_MONTH_COLUMNS: ReadonlySet<string> = new Set(["cost", "profit", "profitPercent"]);

/** The pop-up for one Project performance column heading, in either view. */
export function performanceColumnFigure(
  columnKey: string,
  label: string,
  view: "totals" | "months",
  metrics: readonly PnlMetricOption[],
  options: WidgetDescribeOptions = {},
): MetricDefinition {
  const period =
    view === "months"
      ? "Each month on its own, then the Total for the months shown."
      : "Each project over the whole date range.";
  const metricKey = PERFORMANCE_COLUMN_METRIC[columnKey];
  if (metricKey === undefined || metricKey === null) {
    return {
      name: label,
      meaning: view === "months" ? "One row per project, plus an All projects row on top that adds them together." : "One row per project.",
      formula: [
        "Projects with figures in the date range, after the Projects and Status filters and the name filter above the table.",
        "Select a project name to open its P&L tab.",
      ],
      source: { kind: "calculated", detail: CLAIMS_TYPED },
      projects: PAGE_PROJECTS,
      dates: PAGE_DATES,
      period,
    };
  }
  const metric = metrics.find((m) => m.metricKey === metricKey) ?? { metricKey, section: "calculated" as const, kind: "calculated" as const };
  const described = describeMetric(metric, { projects: PAGE_PROJECTS, dates: PAGE_DATES, period });
  const formula = [...described.formula];
  if (view === "months") {
    formula.push("Month columns: that month on its own. Total: the whole date range read as one span, or only the months shown when Months shown is not All.");
    if (RAW_WAGES_MONTH_COLUMNS.has(columnKey)) {
      formula.push("Month cells use wages as typed while the Total multiplies them, so the months do not add up to the Total.");
    }
  }
  return {
    ...described,
    name: label,
    formula,
    lastUpdated: metricUsesPayroll(metricKey) ? payrollFreshness(options.payrollAsAt) : undefined,
  };
}
